import { createSelector, createSlice, current } from "@reduxjs/toolkit";
import { charDataSelect } from "../../calculators/calculateAll";
import { charInfoFields, rsnNames } from "../../configs";
import {
  findArtifact,
  findCharacter,
  getCharacterData
} from "../../data/controllers";
import {
  ascsFromLv,
  bareLv,
  findByName,
  indexByCode,
  indexByName
} from "../../helpers";
import { processNumInp } from "../../helpers/processors";
import {
  adjustTarget,
  calculate,
  getArtSets,
  getBCsOfWp,
  getMainArtBCs,
  getMainWpBCs,
  getMonsterInputs,
  getNewSetupName,
  getSetupInfo,
  getSubArtBCs,
  getSubArtBCsByCode,
  getSubArtDCs,
  getSubWpBCs,
  getThisChar,
  getVisionCount,
  getWpRange,
  parseData
} from "./helpers";
import {
  initCharMCs,
  initCustomMCs,
  initElmtMCs,
  initMonster,
  initParty,
  initSubWpMCs,
  initTarget,
  initWeapon
} from "./initiators";

export const selectSetups = (state) => state.calculator.setups;

export const selectCurSetupI = (state) => state.calculator.curSetupI;

export const selectChar = createSelector(
  (state) => state.calculator.char,
  (state) => state.calculator.curSetupI,
  (char, curI) => getThisChar(char, curI)
);
export const selectCharData = (state) => state.calculator.charData;

export const selectTarget = (state) => state.calculator.target;

const select = (field) => (state) => {
  return state.calculator[field][state.calculator.curSetupI];
};
export const selectATTRs = select("allATTRs");
export const selectRxnBnes = select("allRxnBnes");
export const selectInfusion = select("allInfusion");
export const selectWeapon = select("wpRack");
export const selectArt = select("artChest");
export const selectParty = select("parties");
export const selectSelfMCs = select("selfMCs");
export const selectElmtMCs = select("elmtMCs");
export const selectSubWpMCs = select("subWpMCs");

export const calculatorSlice = createSlice({
  name: "calculator",
  initialState: {
    curSetupI: 0,
    configs: { sepCharInfo: false, keepArtStats: false },
    setups: [],
    char: null,
    charData: null,
    selfMCs: [],
    wpRack: [],
    subWpMCs: [],
    artChest: [],
    parties: [],
    elmtMCs: [],
    customMCs: [],
    target: initTarget(),
    monster: null,
    allATTRs: [],
    allArtSBnes: [],
    allRxnBnes: [],
    allInfusion: [],
    allDmg: [],
    error: false
  },
  reducers: {
    INIT_SESSION_WITH_CHAR: (state, action) => {
      const { pickedChar, myWps, myArts } = action.payload;
      const [char, wp, art] = parseData(pickedChar, myWps, myArts);
      state.setups = [getSetupInfo({})];
      state.curSetupI = 0;
      state.char = char;
      state.charData = getCharacterData(char, charDataSelect);
      state.selfMCs = [initCharMCs(char.name, true)];
      state.wpRack = [wp];
      state.subWpMCs = [initSubWpMCs()];
      state.artChest = [art];
      state.parties = [initParty()];
      state.elmtMCs = [initElmtMCs()];
      state.customMCs = [initCustomMCs()];
      state.monster = initMonster();
      state.configs.sepCharInfo = false;
      calculate(state);
    },
    LEVEL_CHAR: (state, action) => {
      const level = action.payload;
      const { char, curSetupI } = state;
      const ascension = ascsFromLv(level);

      if (state.target.Level === 1) {
        state.target.Level = bareLv(level);
      }

      if (Array.isArray(char.level)) {
        char.level[curSetupI] = level;
        char.ascension[curSetupI] = ascension;
        calculate(state);
      } else {
        char.level = level;
        char.ascension = ascension;
        calculate(state, "all");
      }
    },
    TOGGLE_CONS: (state, action) => {
      const newCons = action.payload;
      const { char } = state;
      if (Array.isArray(char.constellation)) {
        char.constellation[state.curSetupI] = newCons;
        calculate(state);
      } else {
        char.constellation = newCons;
        calculate(state, "all");
      }
    },
    CHANGE_TALENT: (state, action) => {
      const { tlType, lv } = action.payload;
      const { char } = state;
      if (Array.isArray(char[tlType])) {
        char[tlType][state.curSetupI] = lv;
        calculate(state);
      } else {
        char[tlType] = lv;
        calculate(state, "all");
      }
    },
    TOGGLE_RESONANCE: (state, action) => {
      const { rsnIndex, activated } = action.payload;
      state.elmtMCs[state.curSetupI].resonance[rsnIndex].activated = activated;
      calculate(state);
    },
    CHANGE_RESONANCE_INPUT: (state, action) => {
      const { name, inpIndex, value } = action.payload;
      const rsn = findByName(state.elmtMCs[state.curSetupI].resonance, name);
      if (rsn && rsn.inputs) {
        rsn.inputs[inpIndex] = value;
        calculate(state);
      }
    },
    CHANGE_ELMT_MCS: (state, action) => {
      const { type, value } = action.payload;
      state.elmtMCs[state.curSetupI][type] = value;
      calculate(state);
    },
    // wp
    PICK_WP_IN_DB: (state, action) => {
      const wpInfo = action.payload;
      state.wpRack[state.curSetupI] = { ...wpInfo, BCs: getMainWpBCs(wpInfo) };
      calculate(state);
    },
    CREATE_WP: (state, action) => {
      const item = action.payload;
      const { type, code } = item;
      const { curSetupI } = state;
      const subWpBCs = state.subWpMCs[curSetupI].BCs;

      const oldWeapon = state.wpRack[curSetupI];
      state.wpRack[curSetupI] = { ...item, BCs: getMainWpBCs(item) };

      if (subWpBCs[type]) {
        subWpBCs[type] = subWpBCs[type].filter((ctrl) => ctrl.code !== code);
        subWpBCs[type].push(...getBCsOfWp(oldWeapon));
      }
      calculate(state);
    },
    UPGRADE_WP: (state, action) => {
      state.wpRack[state.curSetupI].level = action.payload;
      calculate(state);
    },
    REFINE_WP: (state, action) => {
      state.wpRack[state.curSetupI].refinement = action.payload;
      calculate(state);
    },
    // art
    COPY_ART: (state, action) => {
      const { artChest } = state;
      artChest[state.curSetupI] = artChest[action.payload];
      calculate(state);
    },
    SET_ART_PIECE: (state, action) => {
      const { pieceIndex, newPiece, isNew } = action.payload;
      const art = state.artChest[state.curSetupI];
      const piece = art.pieces[pieceIndex];
      if (piece && isNew && state.configs.keepArtStats) {
        piece.code = newPiece.code;
        piece.rarity = newPiece.rarity;
      } else {
        art.pieces[pieceIndex] = newPiece;
      }
      const oldSets = art.sets;
      art.sets = getArtSets(art.pieces);
      const oldBonusLv = oldSets[0]?.bonusLv;
      const set = art.sets[0];
      if (set) {
        if (oldBonusLv === 0 && set.bonusLv) {
          art.BCs = getMainArtBCs(set.code);
          const pos = indexByCode(art.subBCs, set.code);
          if (pos !== -1) {
            art.subBCs.splice(pos, 1);
          }
        } else if (oldBonusLv && !set.bonusLv) {
          art.BCs = [];
          const oldSetCode = oldSets[0].code;
          if (findArtifact({ code: oldSetCode }).buffs) {
            art.subBCs.push(...getSubArtBCsByCode(oldSetCode));
          }
        }
      }
      calculate(state);
    },
    SET_ALL_ART_PIECES: (state, action) => {
      const art = state.artChest[state.curSetupI];
      const pieces = action.payload;
      const sets = getArtSets(pieces);
      let newSubBCs = [...art.subBCs];
      const bnLv = sets[0]?.bonusLv;
      if (bnLv) {
        newSubBCs = newSubBCs.filter((ctrl) => ctrl.code !== sets[0].code);
      }
      state.artChest[state.curSetupI] = {
        pieces,
        sets,
        BCs: bnLv ? getMainArtBCs(sets[0].code) : [],
        subBCs: newSubBCs,
        subDCs: art.subDCs
      };
    },
    ENHANCE_ART: (state, action) => {
      const { pieceIndex, level } = action.payload;
      state.artChest[state.curSetupI].pieces[pieceIndex].level = level;
      calculate(state);
    },
    CHANGE_ART_MS_TYPE: (state, action) => {
      const { pieceIndex, type } = action.payload;
      state.artChest[state.curSetupI].pieces[pieceIndex].mainSType = type;
      calculate(state);
    },
    CHANGE_ART_SS_TYPE: (state, action) => {
      const { pieceIndex, ssIndex, type } = action.payload;
      const artPiece = state.artChest[state.curSetupI].pieces[pieceIndex];
      artPiece.subS[ssIndex].type = type;
      calculate(state);
    },
    CHANGE_ART_SS_VAL: (state, action) => {
      const { pieceIndex, ssIndex, value } = action.payload;
      const { subS } = state.artChest[state.curSetupI].pieces[pieceIndex];
      subS[ssIndex].val = processNumInp(value, subS[ssIndex].val, 9999);
      calculate(state);
    },
    // party
    COPY_PARTY: (state, action) => {
      const targetIndex = action.payload;
      const { parties, elmtMCs, subWpMCs, curSetupI } = state;
      parties[curSetupI] = parties[targetIndex];
      elmtMCs[curSetupI] = elmtMCs[targetIndex];
      subWpMCs[curSetupI] = subWpMCs[targetIndex];
      calculate(state);
    },
    ADD_TEAMMATE: (state, action) => {
      const { name, vision, weapon, tmIndex } = action.payload;
      const { char, curSetupI } = state;
      const party = state.parties[curSetupI];
      const elmtMCs = state.elmtMCs[curSetupI];
      const subWpBCs = state.subWpMCs[curSetupI].BCs;

      const oldCount = getVisionCount(char, party);
      const oldRange = getWpRange(party);
      const oldTm = party[tmIndex];
      party[tmIndex] = { name, ...initCharMCs(name, false) };
      const newCount = getVisionCount(char, party);
      const newRange = getWpRange(party);

      if (oldTm) {
        const { vision: oldVision, weapon: oldWeapon } = findCharacter(oldTm);
        if (
          rsnNames[oldVision] &&
          oldCount[oldVision] === 2 &&
          newCount[oldVision] === 1
        ) {
          const index = indexByName(elmtMCs.resonance, rsnNames[oldVision]);
          elmtMCs.resonance.splice(index, 1);
        }
        if (!newRange[oldWeapon]) delete subWpBCs[oldWeapon];
      }
      if (
        rsnNames[vision] &&
        oldCount[vision] === 1 &&
        newCount[vision] === 2
      ) {
        const rsn = {
          name: rsnNames[vision],
          activated: ["Pyro", "Dendro", "Hydro"].includes(vision)
        };
        if (vision === "Dendro") {
          rsn.inputs = [false, false];
        }
        elmtMCs.resonance.push(rsn);
      }
      if (!oldRange[weapon]) {
        subWpBCs[weapon] = getSubWpBCs(weapon, state.wpRack[curSetupI].code);
      }
      calculate(state);
    },
    REMOVE_TEAMMATE: (state, action) => {
      const tmIndex = action.payload;
      const { curSetupI } = state;
      const party = state.parties[curSetupI];
      const elmtMCs = state.elmtMCs[curSetupI];
      const { weapon, vision } = findCharacter(party[tmIndex]);
      party[tmIndex] = null;

      const newCount = getVisionCount(state.char, party);
      const newRange = getWpRange(party);
      if (newCount[vision] === 1) {
        const index = indexByName(elmtMCs.resonance, rsnNames[vision]);
        if (index >= 0) {
          elmtMCs.resonance.splice(index, 1);
        }
      }
      if (!newRange[weapon]) {
        delete state.subWpMCs[curSetupI].BCs[weapon];
      }
      calculate(state);
    },
    // mcs
    TOGGLE_MCS: (state, action) => {
      const { cat, type, mcIndex, activated } = action.payload;
      state[cat][state.curSetupI][type][mcIndex].activated = activated;
      calculate(state);
    },
    CHANGE_MCS_INPUT: (state, action) => {
      const { cat, type, mcIndex, inpIndex, value } = action.payload;
      state[cat][state.curSetupI][type][mcIndex].inputs[inpIndex] = value;
      calculate(state);
    },
    TOGGLE_TM_MCS: (state, action) => {
      const { tmIndex, type, mcIndex, activated } = action.payload;
      const MCs = state.parties[state.curSetupI][tmIndex][type];
      MCs[mcIndex].activated = activated;
      calculate(state);
    },
    CHANGE_TM_MCS_INPUT: (state, action) => {
      const { tmIndex, type, mcIndex, inpIndex, value } = action.payload;
      const MCs = state.parties[state.curSetupI][tmIndex][type];
      MCs[mcIndex].inputs[inpIndex] = value;
      calculate(state);
    },
    TOGGLE_SUB_WP_MCS: (state, action) => {
      const { type, wpType, mcIndex, activated } = action.payload;
      const MCs = state.subWpMCs[state.curSetupI][type][wpType];
      MCs[mcIndex].activated = activated;
      calculate(state);
    },
    CHANGE_SUB_WP_MCS_INPUT: (state, action) => {
      const { type, wpType, mcIndex, inpIndex, value } = action.payload;
      const MCs = state.subWpMCs[state.curSetupI][type][wpType];
      MCs[mcIndex].inputs[inpIndex] = value;
      calculate(state);
    },
    REFINE_SUB_WP: (state, action) => {
      const { type, wpType, mcIndex, value } = action.payload;
      const MCs = state.subWpMCs[state.curSetupI][type][wpType];
      MCs[mcIndex].refinement = value;
      calculate(state);
    },
    // customMCs
    CLEAR_CUSTOM_MCS: (state, action) => {
      state.customMCs[state.curSetupI][action.payload] = [];
      calculate(state);
    },
    COPY_CUSTOM_MCS: (state, action) => {
      const { type, targetIndex } = action.payload;
      const { customMCs } = state;
      customMCs[state.curSetupI][type] = customMCs[targetIndex][type];
      calculate(state);
    },
    REMOVE_CUSTOM_MC: (state, action) => {
      const { type, index } = action.payload;
      state.customMCs[state.curSetupI][type].splice(index, 1);
      calculate(state);
    },
    CHANGE_CUSTOM_MC_VAL: (state, action) => {
      const { type, inpIndex, value } = action.payload;
      state.customMCs[state.curSetupI][type][inpIndex].val = value;
      calculate(state);
    },
    ADD_CUSTOM_MC: (state, action) => {
      const { type, newMC } = action.payload;
      state.customMCs[state.curSetupI][type].unshift(newMC);
      calculate(state);
    },
    // target & monster
    MODIFY_TARGET: (state, action) => {
      const { type, value } = action.payload;
      state.target[type] = value;
      calculate(state, "all");
    },
    CHANGE_MONSTER: (state, action) => {
      const name = action.payload;
      state.monster = { name, inputs: getMonsterInputs(name) };
      adjustTarget(state.target, state.monster);
      calculate(state, "all");
    },
    CHANGE_MONSTER_INPUT: (state, action) => {
      const { inpIndex, value } = action.payload;
      state.monster.inputs[inpIndex] = value;
      adjustTarget(state.target, state.monster);
      calculate(state, "all");
    },
    // setup manager
    CHANGE_CUR_SETUP: (state, action) => {
      state.curSetupI = action.payload;
    },
    APPLY_SETTINGS_CALC: (state, action) => {
      const {
        setups,
        indexes,
        tempConfigs,
        currentI,
        standardI
      } = action.payload;
      const { char, configs } = state;
      function getResult(prev, newElmt) {
        const result = [];
        for (const index of indexes) {
          result.push(index === null ? newElmt : prev[index]);
        }
        return result;
      }
      state.setups = setups;
      state.selfMCs = getResult(state.selfMCs, initCharMCs(char.name, true));
      state.wpRack = getResult(
        state.wpRack,
        (() => {
          const { weapon } = findCharacter(char);
          const newWp = initWeapon({ type: weapon });
          return { ...newWp, BCs: getMainWpBCs(newWp) };
        })()
      );
      state.subWpMCs = getResult(state.subWpMCs, initSubWpMCs());
      state.artChest = getResult(state.artChest, {
        pieces: [null, null, null, null, null],
        sets: [],
        BCs: [],
        subBCs: getSubArtBCs(),
        subDCs: getSubArtDCs()
      });
      state.parties = getResult(state.parties, initParty());
      state.elmtMCs = getResult(state.elmtMCs, initElmtMCs());
      state.customMCs = getResult(state.customMCs, initCustomMCs());
      state.curSetupI = currentI > -1 ? currentI : standardI;

      if (configs.sepCharInfo || tempConfigs.sepCharInfo) {
        for (const field of charInfoFields) {
          if (tempConfigs.sepCharInfo) {
            if (configs.sepCharInfo) {
              char[field] = getResult(char[field], char[field][standardI]);
            } else {
              char[field] = Array(indexes.length).fill(char[field]);
            }
          } else if (configs.sepCharInfo) {
            char[field] = char[field][standardI];
          }
        }
      }
      state.configs = tempConfigs;
      calculate(state, "all");
    },
    IMPORT_SETUP: (state, action) => {
      const { imported, overwriteChar, overwriteTg } = action.payload;
      const { char, setups } = state;
      const { sepCharInfo } = state.configs;
      const { ID, type } = imported;
      state.setups.push(
        getSetupInfo({ name: getNewSetupName(setups), ID, type })
      );
      for (const field of charInfoFields) {
        if (overwriteChar) {
          if (sepCharInfo) {
            char[field] = Array(setups.length).fill(imported.char[field]);
          } else {
            char[field] = imported.char[field];
          }
        } else if (sepCharInfo) {
          char[field].push(imported.char[field]);
        }
      }
      if (overwriteTg) state.target = imported.target;
      state.selfMCs.push(imported.selfMCs);
      state.wpRack.push(imported.weapon);
      state.subWpMCs.push(imported.subWpMCs);
      state.artChest.push(imported.art);
      state.parties.push(imported.party);
      state.elmtMCs.push(imported.elmtMCs);
      state.customMCs.push(imported.customMCs);
      state.curSetupI = state.wpRack.length - 1;
      calculate(state, "all");
    },
    INIT_SESSION_WITH_SETUP: (state, action) => {
      const imported = action.payload;
      state.setups = [getSetupInfo({ ID: imported.ID, type: imported.type })];
      state.char = imported.char;
      state.charData = getCharacterData(imported.char, charDataSelect);
      state.selfMCs = [imported.selfMCs];
      state.wpRack = [imported.weapon];
      state.subWpMCs = [imported.subWpMCs];
      state.artChest = [imported.art];
      state.parties = [imported.party];
      state.elmtMCs = [imported.elmtMCs];
      state.customMCs = [imported.customMCs];
      state.target = imported.target;
      state.monster = initMonster();
      state.configs.sepCharInfo = false;
      state.curSetupI = 0;
      calculate(state);
    },
    CLOSE_ERROR: (state) => {
      state.error = false;
    }
  }
});

export const {
  INIT_SESSION_WITH_CHAR,
  LEVEL_CHAR,
  TOGGLE_CONS,
  CHANGE_TALENT,
  PICK_WP_IN_DB,
  CREATE_WP,
  UPGRADE_WP,
  REFINE_WP,
  COPY_ART,
  SET_ART_PIECE,
  SET_ALL_ART_PIECES,
  ENHANCE_ART,
  CHANGE_ART_MS_TYPE,
  CHANGE_ART_SS_TYPE,
  CHANGE_ART_SS_VAL,
  COPY_PARTY,
  ADD_TEAMMATE,
  REMOVE_TEAMMATE,
  TOGGLE_MCS,
  CHANGE_MCS_INPUT,
  TOGGLE_TM_MCS,
  CHANGE_TM_MCS_INPUT,
  TOGGLE_SUB_WP_MCS,
  CHANGE_SUB_WP_MCS_INPUT,
  REFINE_SUB_WP,
  TOGGLE_RESONANCE,
  CHANGE_RESONANCE_INPUT,
  CHANGE_ELMT_MCS,
  CLEAR_CUSTOM_MCS,
  COPY_CUSTOM_MCS,
  REMOVE_CUSTOM_MC,
  CHANGE_CUSTOM_MC_VAL,
  ADD_CUSTOM_MC,
  MODIFY_TARGET,
  CHANGE_MONSTER,
  CHANGE_MONSTER_INPUT,
  CHANGE_CUR_SETUP,
  APPLY_SETTINGS_CALC,
  IMPORT_SETUP,
  INIT_SESSION_WITH_SETUP,
  CLOSE_ERROR
} = calculatorSlice.actions;

export default calculatorSlice.reducer;
